import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  ValidateFormulaDto,
  ValidateFormulaResponseDto,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../../../../utils/api";
import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";
import {
  FORMULA_FUNCTION_ARITY,
  FORMULA_FUNCTION_NAMES,
  FormulaBinaryOp,
  FormulaFunctionName,
  FormulaNode,
  SignalMetadata,
  RecommendationEngineEndpoint,
} from "../../../../types/recommendationEngine";
import {
  astToDisplayString,
  astToTermList,
  createDefaultFormulaNode,
  createDefaultFunctionNode,
  FormulaTerm,
  SINGLE_ARG_FUNCTION_NAMES,
  termListToAst,
} from "../../../../utils/recommendationEngine/formulaAst";
import {
  astToFormulaText,
  parseFormulaText,
} from "../../../../utils/recommendationEngine/formulaText";
import FormulaTextEditorTab from "./FormulaTextEditorTab";
import SignalDetailMenuItem from "./SignalDetailMenuItem";
import InfoTooltip from "./InfoTooltip";
import {
  FIELD_TOOLTIPS,
  FORMULA_TOOLTIPS,
  FUNCTION_DESCRIPTIONS,
  SIGNAL_GROUP_DESCRIPTIONS,
  VALIDATION_MESSAGE_TEMPLATES,
} from "../copy";
import { alpha } from "@mui/material/styles";
import { engineTokens } from "../theme";

type Props = {
  open: boolean;
  initialFormula: FormulaNode;
  endpoint: RecommendationEngineEndpoint;
  signals: SignalMetadata[];
  clamp01: boolean;
  onClose: () => void;
  onApply: (formula: FormulaNode) => void;
};

const OPS: FormulaBinaryOp[] = ["+", "-", "*", "/"];

const TEXT_EDITOR_TAB = 0;
const TERM_LIST_TAB = 1;
const EXPRESSION_TREE_TAB = 2;

const toValidateEndpointEnum = (endpoint: RecommendationEngineEndpoint) => {
  switch (endpoint) {
    case "similar":
      return ValidateFormulaDto.EndpointEnum.Similar;
    case "recommended":
      return ValidateFormulaDto.EndpointEnum.Recommended;
    case "nearby_picks":
      return ValidateFormulaDto.EndpointEnum.NearbyPicks;
    case "trending_now":
      return ValidateFormulaDto.EndpointEnum.TrendingNow;
    case "browse_by_interest":
      return ValidateFormulaDto.EndpointEnum.BrowseByInterest;
    case "smart_picks":
      return ValidateFormulaDto.EndpointEnum.SmartPicks;
    default:
      return ValidateFormulaDto.EndpointEnum.Recommended;
  }
};

const FormulaEditorModal = ({
  open,
  initialFormula,
  endpoint,
  signals,
  clamp01,
  onClose,
  onApply,
}: Props) => {
  const authHeaders = useAuthHeaders();
  const [tab, setTab] = useState(TEXT_EDITOR_TAB);
  const [formula, setFormula] = useState<FormulaNode>(initialFormula);
  const [op, setOp] = useState<FormulaBinaryOp>("+");
  const [terms, setTerms] = useState<FormulaTerm[]>([{ value: 1, signalId: "" }]);
  const [validation, setValidation] = useState<ValidateFormulaResponseDto | null>(
    null,
  );
  const [isValidating, setIsValidating] = useState(false);
  const [formulaText, setFormulaText] = useState("");
  const [textParseError, setTextParseError] = useState<string | null>(null);

  const filteredSignals = useMemo(
    () =>
      signals.filter((s) => s.applicableEndpoints?.includes(endpoint)),
    [signals, endpoint],
  );

  const signalIds = useMemo(
    () => filteredSignals.map((s) => s.id),
    [filteredSignals],
  );

  const groupedSignals = useMemo(() => {
    const groups: Record<string, SignalMetadata[]> = {};
    filteredSignals.forEach((s) => {
      if (!groups[s.group]) {
        groups[s.group] = [];
      }
      groups[s.group].push(s);
    });
    return groups;
  }, [filteredSignals]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormula(initialFormula);
    setFormulaText(astToFormulaText(initialFormula));
    setTextParseError(null);
    const parsed = astToTermList(initialFormula);
    if (parsed) {
      setOp(parsed.op);
      setTerms(parsed.terms.length ? parsed.terms : [{ value: 1, signalId: "" }]);
    }
    setTab(TEXT_EDITOR_TAB);
    setValidation(null);
  }, [open, initialFormula]);

  const resolveActiveFormula = (): FormulaNode | null => {
    if (tab !== TEXT_EDITOR_TAB) {
      return formula;
    }
    const { node, error } = parseFormulaText(formulaText, signalIds);
    setTextParseError(error);
    return node;
  };

  const handleTabChange = (_: React.SyntheticEvent, nextTab: number) => {
    if (tab === TEXT_EDITOR_TAB && nextTab !== TEXT_EDITOR_TAB) {
      const { node, error } = parseFormulaText(formulaText, signalIds);
      if (error) {
        setTextParseError(error);
      } else if (node) {
        setFormula(node);
        setTextParseError(null);
      }
    }
    if (nextTab === TEXT_EDITOR_TAB) {
      setFormulaText(astToFormulaText(formula));
      setTextParseError(null);
    }
    setTab(nextTab);
  };

  const syncTermListToFormula = (
    nextOp: FormulaBinaryOp,
    nextTerms: FormulaTerm[],
  ) => {
    setOp(nextOp);
    setTerms(nextTerms);
    setFormula(termListToAst(nextOp, nextTerms));
  };

  const handleValidate = async () => {
    const activeFormula = resolveActiveFormula();
    if (!activeFormula) {
      return;
    }
    if (tab === TEXT_EDITOR_TAB) {
      setFormula(activeFormula);
    }
    setIsValidating(true);
    try {
      const body: ValidateFormulaDto = {
        endpoint: toValidateEndpointEnum(endpoint),
        formula: activeFormula,
      };
      const result =
        await adminApiService.recommendationEngineAdminControllerValidateFormula(
          body,
          authHeaders,
        );
      setValidation(result);
    } catch {
      setValidation({
        valid: false,
        errors: ["Validation request failed."],
        warnings: [],
        signalIds: [],
      });
    } finally {
      setIsValidating(false);
    }
  };

  const renderSignalSelect = (
    value: string,
    onChange: (signalId: string) => void,
    label?: string,
  ) => (
    <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        value={value}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(selected) => {
          const id = selected as string;
          if (!id) return "";
          const match = filteredSignals.find((s) => s.id === id);
          if (!match) return id;
          return (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" component="span">
                {match.label}
              </Typography>
              {match.isRaw && clamp01 && (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    bgcolor: alpha(engineTokens.warning, 0.2),
                    color: "#92400E",
                    px: 0.5,
                    borderRadius: 0.5,
                    fontWeight: 700,
                  }}
                >
                  RAW
                </Typography>
              )}
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: 480, maxWidth: 480 },
          },
        }}
      >
        {Object.entries(groupedSignals).flatMap(([group, items]) => [
          <MenuItem
            key={`${group}-header`}
            disabled
            sx={{ opacity: 0.7, alignItems: "flex-start" }}
          >
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {group.toUpperCase()}
              </Typography>
              {SIGNAL_GROUP_DESCRIPTIONS[group] && (
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ whiteSpace: "normal" }}
                >
                  {SIGNAL_GROUP_DESCRIPTIONS[group]}
                </Typography>
              )}
            </Box>
          </MenuItem>,
          ...items.map((s) => (
            <SignalDetailMenuItem
              key={s.id}
              signal={s}
              value={s.id}
              clamp01={clamp01}
            />
          )),
        ])}
      </Select>
    </FormControl>
  );

  const renderTreeEditor = (
    node: FormulaNode,
    onChange: (n: FormulaNode) => void,
    depth = 0,
  ): React.ReactNode => {
    if (depth > 20) {
      return <Typography color="error">Max depth reached</Typography>;
    }

    if (node.type === "literal") {
      return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", minWidth: 80 }}>
            <Typography variant="caption">Literal</Typography>
            <InfoTooltip title={FORMULA_TOOLTIPS.literalNode} />
          </Box>
          <TextField
            size="small"
            type="number"
            value={node.value}
            onChange={(e) =>
              onChange({ type: "literal", value: Number(e.target.value) })
            }
          />
          <Tooltip title="Convert this node to a signal">
            <Button
              size="small"
              onClick={() =>
                onChange({
                  type: "signal",
                  signalId: filteredSignals[0]?.id ?? "",
                })
              }
            >
              → Signal
            </Button>
          </Tooltip>
          <Tooltip title="Wrap this node in a function call">
            <Button
              size="small"
              onClick={() =>
                onChange({
                  type: "function",
                  name: "sqrt",
                  args: [node],
                })
              }
            >
              → Function
            </Button>
          </Tooltip>
        </Box>
      );
    }

    if (node.type === "signal") {
      return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", minWidth: 80 }}>
            <Typography variant="caption">Signal</Typography>
            <InfoTooltip title={FORMULA_TOOLTIPS.signalNode} />
          </Box>
          {renderSignalSelect(node.signalId, (signalId) =>
            onChange({ type: "signal", signalId }),
          )}
          <Tooltip title="Wrap this signal in a function call">
            <Button
              size="small"
              onClick={() =>
                onChange({
                  type: "function",
                  name: "sqrt",
                  args: [node],
                })
              }
            >
              → Function
            </Button>
          </Tooltip>
        </Box>
      );
    }

    if (node.type === "function") {
      const arity = FORMULA_FUNCTION_ARITY[node.name];
      const canAdd = node.args.length < arity.max;
      const canRemove = node.args.length > arity.min;
      return (
        <Box
          sx={{
            border: `1px solid ${alpha(engineTokens.brand, 0.4)}`,
            borderRadius: 2,
            p: 1.5,
            mb: 1,
            ml: depth * 2,
            backgroundColor: engineTokens.brandSofter,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 1,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                Function
              </Typography>
              <InfoTooltip title={FORMULA_TOOLTIPS.functionNode} />
            </Box>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={node.name}
                onChange={(e) => {
                  const nextName = e.target.value as FormulaFunctionName;
                  const nextArity = FORMULA_FUNCTION_ARITY[nextName];
                  const nextArgs = [...node.args];
                  while (nextArgs.length < nextArity.min) {
                    nextArgs.push(createDefaultFormulaNode());
                  }
                  while (
                    nextArgs.length > nextArity.max &&
                    nextArity.max !== Infinity
                  ) {
                    nextArgs.pop();
                  }
                  onChange({
                    type: "function",
                    name: nextName,
                    args: nextArgs,
                  });
                }}
              >
                {FORMULA_FUNCTION_NAMES.map((fn) => (
                  <MenuItem key={fn} value={fn}>
                    {fn}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={FUNCTION_DESCRIPTIONS[node.name]}>
              <Typography variant="caption" color="text.secondary">
                {FUNCTION_DESCRIPTIONS[node.name]}
              </Typography>
            </Tooltip>
            <Tooltip title="Replace this function with its first argument">
              <span>
                <Button
                  size="small"
                  onClick={() => onChange(node.args[0] ?? createDefaultFormulaNode())}
                >
                  Unwrap
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Box sx={{ display: "inline-flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Arguments
            </Typography>
            <InfoTooltip title={FORMULA_TOOLTIPS.functionArgs} />
          </Box>
          {node.args.map((arg, index) => (
            <Box
              key={index}
              sx={{
                border: `1px dashed ${alpha(engineTokens.brand, 0.4)}`,
                borderRadius: 1.5,
                p: 1,
                mb: 0.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption">
                  Argument {index + 1}
                </Typography>
                {canRemove && (
                  <IconButton
                    size="small"
                    aria-label={`Remove argument ${index + 1}`}
                    onClick={() => {
                      const nextArgs = node.args.filter((_, i) => i !== index);
                      onChange({ ...node, args: nextArgs });
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {renderTreeEditor(
                arg,
                (next) => {
                  const nextArgs = node.args.map((a, i) =>
                    i === index ? next : a,
                  );
                  onChange({ ...node, args: nextArgs });
                },
                depth + 1,
              )}
            </Box>
          ))}
          {canAdd && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                onChange({
                  ...node,
                  args: [...node.args, createDefaultFormulaNode()],
                })
              }
            >
              Add argument
            </Button>
          )}
          {!canAdd && arity.max !== Infinity && (
            <Typography variant="caption" color="text.secondary">
              This function takes exactly {arity.max} argument
              {arity.max === 1 ? "" : "s"}.
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          border: `1px solid ${engineTokens.border}`,
          borderRadius: 2,
          p: 1.5,
          mb: 1,
          ml: depth * 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <Box sx={{ display: "inline-flex", alignItems: "center" }}>
            <Typography variant="caption">Binary</Typography>
            <InfoTooltip title={FORMULA_TOOLTIPS.binaryNode} />
          </Box>
          <FormControl size="small">
            <Select
              value={node.op}
              onChange={(e) =>
                onChange({
                  ...node,
                  op: e.target.value as FormulaBinaryOp,
                })
              }
            >
              {OPS.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <InfoTooltip title={FORMULA_TOOLTIPS.operator} />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Left
        </Typography>
        {renderTreeEditor(node.left, (left) => onChange({ ...node, left }), depth + 1)}
        <Typography variant="caption" color="text.secondary">
          Right
        </Typography>
        {renderTreeEditor(
          node.right,
          (right) => onChange({ ...node, right }),
          depth + 1,
        )}
        <Box sx={{ display: "flex", gap: 0.5, mt: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            onClick={() =>
              onChange({
                type: "binary",
                op: "+",
                left: node,
                right: createDefaultFormulaNode(),
              })
            }
          >
            + Wrap in binary
          </Button>
          <Button
            size="small"
            onClick={() =>
              onChange(createDefaultFunctionNode("sqrt"))
            }
          >
            Replace with function
          </Button>
        </Box>
      </Box>
    );
  };

  const successHeader = VALIDATION_MESSAGE_TEMPLATES.successHeader;
  const failureHeader = VALIDATION_MESSAGE_TEMPLATES.failureHeader;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit formula</DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Text editor" value={TEXT_EDITOR_TAB} />
          <Tab label="Term list" value={TERM_LIST_TAB} />
          <Tab label="Expression tree" value={EXPRESSION_TREE_TAB} />
        </Tabs>

        {tab === TERM_LIST_TAB && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Combine with</InputLabel>
                <Select
                  label="Combine with"
                  value={op}
                  onChange={(e) =>
                    syncTermListToFormula(
                      e.target.value as FormulaBinaryOp,
                      terms,
                    )
                  }
                >
                  {OPS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <InfoTooltip title={FORMULA_TOOLTIPS.combineWith} />
            </Box>
            <Box sx={{ display: "inline-flex", alignItems: "center", mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Each row is one weighted signal term
              </Typography>
              <InfoTooltip title={FORMULA_TOOLTIPS.term} />
            </Box>
            {terms.map((term, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TextField
                    size="small"
                    type="number"
                    label="Weight"
                    value={term.value}
                    onChange={(e) => {
                      const next = [...terms];
                      next[index] = { ...term, value: Number(e.target.value) };
                      syncTermListToFormula(op, next);
                    }}
                    sx={{ width: 110 }}
                  />
                  <InfoTooltip title={FORMULA_TOOLTIPS.weight} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>Function</InputLabel>
                    <Select
                      label="Function"
                      value={term.functionName ?? ""}
                      onChange={(e) => {
                        const next = [...terms];
                        const raw = e.target.value;
                        next[index] = {
                          ...term,
                          functionName: raw
                            ? (raw as FormulaTerm["functionName"])
                            : undefined,
                        };
                        syncTermListToFormula(op, next);
                      }}
                      renderValue={(selected) =>
                        (selected as string) || "none"
                      }
                    >
                      <MenuItem value="">
                        <em>none</em>
                      </MenuItem>
                      {SINGLE_ARG_FUNCTION_NAMES.map((fn) => (
                        <MenuItem key={fn} value={fn}>
                          <Box>
                            <Typography variant="body2">{fn}</Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ whiteSpace: "normal" }}
                            >
                              {FUNCTION_DESCRIPTIONS[fn]}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <InfoTooltip title={FORMULA_TOOLTIPS.fn} />
                </Box>
                {renderSignalSelect(term.signalId, (signalId) => {
                  const next = [...terms];
                  next[index] = { ...term, signalId };
                  syncTermListToFormula(op, next);
                })}
                <IconButton
                  size="small"
                  onClick={() => {
                    const next = terms.filter((_, i) => i !== index);
                    syncTermListToFormula(
                      op,
                      next.length ? next : [{ value: 1, signalId: "" }],
                    );
                  }}
                  disabled={terms.length <= 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={() =>
                syncTermListToFormula(op, [...terms, { value: 1, signalId: "" }])
              }
            >
              Add term
            </Button>
          </Box>
        )}

        {tab === EXPRESSION_TREE_TAB && (
          <Box>
            <Alert severity="info" sx={{ mb: 1 }}>
              Expression tree lets you nest binary operators and math functions
              (sqrt, min, max, etc.) for advanced scoring shapes.
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Preview: {astToDisplayString(formula)}
            </Typography>
            {renderTreeEditor(formula, setFormula)}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                size="small"
                onClick={() => setFormula(createDefaultFormulaNode())}
              >
                Reset to literal 0
              </Button>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Wrap whole formula in</InputLabel>
                <Select
                  value=""
                  label="Wrap whole formula in"
                  displayEmpty
                  onChange={(e) => {
                    const fn = e.target.value as FormulaFunctionName;
                    if (!fn) return;
                    const arity = FORMULA_FUNCTION_ARITY[fn];
                    const extraArgs = Math.max(0, arity.min - 1);
                    setFormula({
                      type: "function",
                      name: fn,
                      args: [
                        formula,
                        ...Array.from({ length: extraArgs }, () =>
                          createDefaultFormulaNode(),
                        ),
                      ],
                    });
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Pick a function…</em>
                  </MenuItem>
                  {FORMULA_FUNCTION_NAMES.map((fn) => (
                    <MenuItem key={fn} value={fn}>
                      <Box>
                        <Typography variant="body2">
                          {fn}({"…"})
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: "normal" }}
                        >
                          {FUNCTION_DESCRIPTIONS[fn]}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Replace with function</InputLabel>
                <Select
                  value=""
                  label="Replace with function"
                  displayEmpty
                  onChange={(e) => {
                    const fn = e.target.value as FormulaFunctionName;
                    if (!fn) return;
                    setFormula(createDefaultFunctionNode(fn));
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Start fresh with…</em>
                  </MenuItem>
                  {FORMULA_FUNCTION_NAMES.map((fn) => (
                    <MenuItem key={fn} value={fn}>
                      <Box>
                        <Typography variant="body2">{fn}</Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: "normal" }}
                        >
                          {FUNCTION_DESCRIPTIONS[fn]}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {tab === TEXT_EDITOR_TAB && (
          <FormulaTextEditorTab
            formulaText={formulaText}
            onFormulaTextChange={(text) => {
              setFormulaText(text);
              setTextParseError(null);
            }}
            parseError={textParseError}
            signals={filteredSignals}
            clamp01={clamp01}
          />
        )}

        {validation && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={validation.valid ? "success" : "error"}>
              {validation.valid ? successHeader : failureHeader}
              {validation.valid && validation.signalIds && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body2">
                    {VALIDATION_MESSAGE_TEMPLATES.signalsLine(
                      validation.signalIds,
                    )}
                  </Typography>
                </Box>
              )}
            </Alert>
            {validation.warnings && validation.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {validation.warnings.map((w) => (
                  <Typography key={w} variant="body2">
                    {w}
                  </Typography>
                ))}
              </Alert>
            )}
            {validation.errors && validation.errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {validation.errors.map((err) => (
                  <Typography key={err} variant="body2">
                    {err}
                  </Typography>
                ))}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Tooltip title={FIELD_TOOLTIPS.formulaValidate} placement="top" arrow>
          <span>
            <Button onClick={handleValidate} disabled={isValidating}>
              Validate
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const activeFormula = resolveActiveFormula();
            if (!activeFormula) {
              return;
            }
            onApply(activeFormula);
            onClose();
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormulaEditorModal;
